import { useEffect, useRef, useState } from "react";

export const hasBvrBridge = () =>
  typeof window.flutter_inappwebview?.callHandler === "function";

const createReference = () => {
  const bytes = new Uint8Array(16);
  window.crypto.getRandomValues(bytes);
  return Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
};

export default function useBvrBridge() {
  const [ready, setReady] = useState(hasBvrBridge);
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState(null);
  const [error, setError] = useState("");
  const [reference, setReference] = useState("");
  const inFlight = useRef(false);
  const mounted = useRef(true);

  useEffect(() => {
    mounted.current = true;
    const onReady = () => setReady(hasBvrBridge());
    window.addEventListener("flutterInAppWebViewPlatformReady", onReady);
    onReady();
    return () => {
      mounted.current = false;
      window.removeEventListener("flutterInAppWebViewPlatformReady", onReady);
    };
  }, []);

  const start = async () => {
    if (inFlight.current || !hasBvrBridge()) return;
    inFlight.current = true;
    setPending(true);
    setError("");
    setResult(null);
    setReference("");
    try {
      const refNo = createReference();
      setReference(refNo);
      const response = await window.flutter_inappwebview.callHandler("startBvr", {
        refNo,
      });
      if (!response || typeof response.success !== "boolean") {
        throw new Error("Invalid bridge response");
      }
      if (mounted.current) setResult(response);
    } catch {
      if (mounted.current) {
        setError("Unable to complete verification. Please try again.");
      }
    } finally {
      inFlight.current = false;
      if (mounted.current) setPending(false);
    }
  };

  return { ready, pending, result, error, reference, start, clearResult: () => { setResult(null); setError(""); } };
}
